import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SwatchPicker, OptionGroup, SizeSlider } from './SwatchPicker'
import {
  OVERLAY_FONTS,
  GRADIENT_PRESETS,
  OUTLINE_COLORS,
  ensureFontLoaded,
  renderTextMask,
  composeOverlay,
  autoOverlapColorId,
  resolveGradientPreset,
  resolveOutlineColor,
  unionMasks,
  makeSilhouetteOutline,
  buildLegend,
} from '../utils/textRender'

const BOTTOM_SIZE_RANGE = { min: 16, max: 64 }
const TOP_SIZE_RANGE = { min: 6, max: 24 }

function drawPattern(canvas, pattern, brand) {
  if (!pattern || !canvas) return
  const { grid, width, height } = pattern
  const cell = Math.max(4, Math.floor(640 / Math.max(width, height)))
  canvas.width = width * cell
  canvas.height = height * cell
  const ctx = canvas.getContext('2d')
  const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      ctx.fillStyle = colorMap.get(grid[y][x]) || '#fffdf8'
      ctx.fillRect(x * cell, y * cell, cell, cell)
    }
  }
}

export default function TextOverlayModal({ open, brand, onClose, onConfirm, initial }) {
  const { isPermanent } = useAuth()
  // 按色号名查找（默认色基于 MARD 色号；其他品牌无此色号时回退到现有默认）
  const byName = (name) => brand.colors.find((c) => c.name === name)?.id
  const defaultC = byName('B07')
  const [bottomText, setBottomText] = useState('豆田造物')
  const [topText, setTopText] = useState('好玩好用')
  const [bottomFontKey, setBottomFontKey] = useState('huangyou')
  const [topFontKey, setTopFontKey] = useState('zhimangxing')
  const [bottomHeight, setBottomHeight] = useState(30)
  const [topHeight, setTopHeight] = useState(17)
  const [colorMode, setColorMode] = useState('solid') // 'solid' | 渐变预设 key
  const [colorAId, setColorAId] = useState(byName('B29') || brand.colors[0]?.id)
  const [outlineEnabled, setOutlineEnabled] = useState(false)
  const [outlineColorKey, setOutlineColorKey] = useState('black')
  const [outlineThickness, setOutlineThickness] = useState(1)
  const [outlineGap, setOutlineGap] = useState(1)
  const [colorBId, setColorBId] = useState(byName('C14') || brand.colors[1]?.id || brand.colors[0]?.id)
  const [colorCId, setColorCId] = useState(defaultC || null)
  // 叠加色默认推荐 B07：已有时视为“用户已选定”，不随 A/B 变化自动重算；其他品牌回退自动推荐
  const [colorCTouched, setColorCTouched] = useState(Boolean(defaultC))
  const [dx, setDx] = useState(0)
  const [dy, setDy] = useState(0)
  const [fontTick, setFontTick] = useState(0)
  const [previewPattern, setPreviewPattern] = useState(null)
  const previewCanvasRef = useRef(null)

  const colorMap = useMemo(() => new Map(brand.colors.map((c) => [c.id, c])), [brand])
  const activePreset = GRADIENT_PRESETS.find((p) => p.key === colorMode) || null
  // 渐变模式下底面颜色 = 预设三色映射到品牌色板
  const gradientIds = useMemo(
    () => (activePreset ? resolveGradientPreset(activePreset, brand.colors) : null),
    [activePreset, brand]
  )
  // 描边色映射到品牌实色
  const outlineColorId = useMemo(() => {
    const preset = OUTLINE_COLORS.find((c) => c.key === outlineColorKey) || OUTLINE_COLORS[0]
    return resolveOutlineColor(preset.hex, brand.colors)
  }, [outlineColorKey, brand])
  const bottomFont = OVERLAY_FONTS.find((f) => f.key === bottomFontKey) || OVERLAY_FONTS[0]
  const topFont = OVERLAY_FONTS.find((f) => f.key === topFontKey) || OVERLAY_FONTS[0]

  // 打开/切换字体时按需加载字体，完成后触发重绘
  useEffect(() => {
    if (!open) return
    let cancelled = false
    Promise.all([
      ensureFontLoaded(bottomFont, bottomText || '拼豆'),
      ensureFontLoaded(topFont, topText || '拼豆'),
    ]).then(() => {
      if (!cancelled) setFontTick((t) => t + 1)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bottomFontKey, topFontKey])

  // 重新编辑：打开时回填上次生成时的配置
  useEffect(() => {
    if (!open || !initial) return
    setBottomText(initial.bottomText ?? '豆田造物')
    setTopText(initial.topText ?? '好玩好用')
    setBottomFontKey(initial.bottomFontKey ?? 'huangyou')
    setTopFontKey(initial.topFontKey ?? 'zhimangxing')
    setBottomHeight(initial.bottomHeight ?? 30)
    setTopHeight(initial.topHeight ?? 17)
    setColorMode(initial.colorMode ?? 'solid')
    setColorAId(initial.colorAId ?? byName('B29') ?? brand.colors[0]?.id)
    setColorBId(initial.colorBId ?? byName('C14') ?? brand.colors[1]?.id ?? brand.colors[0]?.id)
    setColorCId(initial.colorCId ?? defaultC ?? null)
    setColorCTouched(initial.colorCTouched ?? Boolean(defaultC))
    setOutlineEnabled(Boolean(initial.outlineEnabled))
    setOutlineColorKey(initial.outlineColorKey ?? 'black')
    setOutlineThickness(initial.outlineThickness ?? 1)
    setOutlineGap(initial.outlineGap ?? 1)
    setDx(initial.dx ?? 0)
    setDy(initial.dy ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // A/B 颜色变化且用户未手动指定叠加色时，自动推荐叠加色（渐变时取中间色参与推荐）
  useEffect(() => {
    if (colorCTouched) return
    const aHex = activePreset ? activePreset.colors[1] : colorMap.get(colorAId)?.hex
    const bHex = colorMap.get(colorBId)?.hex
    if (aHex && bHex) {
      const auto = autoOverlapColorId(aHex, bHex, brand.colors)
      if (auto) setColorCId(auto)
    }
  }, [colorAId, colorBId, colorCTouched, colorMap, brand, activePreset])

  const generatePattern = () => {
    if (!bottomText.trim() || !topText.trim()) return null
    const bottom = renderTextMask(bottomText.trim(), bottomFont, bottomHeight)
    const top = renderTextMask(topText.trim(), topFont, topHeight)
    if (!bottom || !top) return null

    // 顶层默认居中，dx/dy 逐格微调
    const cx = Math.round((bottom.w - top.w) / 2)
    const cy = Math.round((bottom.h - top.h) / 2)
    // 描边基于底面+顶面文字的整体内容计算（顶字位置变化时整体边缘随之重算）
    const outline = outlineEnabled && outlineColorId
      ? makeSilhouetteOutline(unionMasks(bottom, top, cx + dx, cy + dy), outlineThickness, bottomHeight, outlineGap)
      : null
    const composed = composeOverlay({
      bottom,
      top,
      topX: cx + dx,
      topY: cy + dy,
      colorA: gradientIds || colorAId,
      colorB: colorBId,
      colorC: colorCId || colorAId,
      outline,
      outlineColor: outlineColorId,
      pad: 1,
    })
    if (!composed) return null

    const { legend, total } = buildLegend(composed.grid, brand)
    return { grid: composed.grid, legend, width: composed.width, height: composed.height, total, brandId: brand.id }
  }

  useEffect(() => {
    if (!open) return
    const pattern = generatePattern()
    setPreviewPattern(pattern)
    drawPattern(previewCanvasRef.current, pattern, brand)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bottomText, topText, bottomFontKey, topFontKey, bottomHeight, topHeight, colorMode, colorAId, colorBId, colorCId, gradientIds, outlineEnabled, outlineColorId, outlineThickness, outlineGap, dx, dy, brand, fontTick])

  if (!open) return null

  // 永久会员专属（入口已拦截，这里兜底）
  if (!isPermanent) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-card">
          <p className="mb-2 text-3xl">🔒</p>
          <h3 className="mb-2 text-lg font-bold text-cocoa">叠加文字图纸</h3>
          <p className="mb-4 text-sm text-cocoa-light">该功能为永久会员专属，升级后即刻解锁双层文字创意玩法~</p>
          <div className="flex justify-center gap-2">
            <button onClick={onClose} className="rounded-full border border-lavender/60 px-5 py-2 text-sm font-medium text-cocoa transition hover:bg-lavender/30">
              关闭
            </button>
            <Link to="/pricing" className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark">
              升级永久会员
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const nudge = (ddx, ddy) => {
    setDx((v) => Math.max(-50, Math.min(50, v + ddx)))
    setDy((v) => Math.max(-50, Math.min(50, v + ddy)))
  }

  const fontOptions = OVERLAY_FONTS.map((f) => ({ value: f.key, label: f.label }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[95vh] w-full max-w-3xl flex-col gap-4 overflow-auto rounded-3xl bg-white p-5 shadow-card">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-cocoa">叠加文字图纸 <span className="ml-1 rounded-full bg-peach px-2 py-0.5 text-xs font-semibold text-cocoa">永久会员专属</span></h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-cocoa-light transition hover:bg-cream-100 hover:text-cocoa"
          >
            ✕
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-cocoa-light">底面文字（大字，支持换行，换行后每行居中）</label>
              <textarea
                value={bottomText}
                onChange={(e) => { setBottomText(e.target.value); setDx(0); setDy(0) }}
                maxLength={24}
                rows={2}
                className="form-input resize-none"
                placeholder={'例如：豆田造物\n（回车换行）'}
              />
            </div>
            <OptionGroup label="底面字体" value={bottomFontKey} onChange={setBottomFontKey} options={fontOptions} />
            <SizeSlider
              label="底面字号（无级调节）"
              value={bottomHeight}
              onChange={setBottomHeight}
              min={BOTTOM_SIZE_RANGE.min}
              max={BOTTOM_SIZE_RANGE.max}
            />
            <div>
              <label className="mb-1 block text-xs font-medium text-cocoa-light">底面配色</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setColorMode('solid')}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                    colorMode === 'solid'
                      ? 'border-blush bg-blush text-white'
                      : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                  }`}
                >
                  单色
                </button>
                {GRADIENT_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setColorMode(p.key)}
                    className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition ${
                      colorMode === p.key
                        ? 'border-blush bg-blush text-white'
                        : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                    }`}
                  >
                    <span className="inline-flex w-4 flex-col overflow-hidden rounded-sm border border-black/10">
                      {p.colors.map((hex) => (
                        <span key={hex} className="h-1.5 w-full" style={{ backgroundColor: hex }} />
                      ))}
                    </span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {colorMode === 'solid' && (
              <SwatchPicker label="底面文字颜色" value={colorAId} onChange={setColorAId} colors={brand.colors} />
            )}
            <div>
              <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-cocoa-light">
                <input
                  type="checkbox"
                  checked={outlineEnabled}
                  onChange={(e) => setOutlineEnabled(e.target.checked)}
                  className="h-4 w-4 accent-blush"
                />
                整体描边（基于底面+顶面文字的整体轮廓，柔性圆角）
              </label>
              {outlineEnabled && (
                <div className="mt-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-cocoa-light">厚度</span>
                    {[1, 2, 3].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setOutlineThickness(t)}
                        className={`rounded-lg border px-3 py-1 text-xs font-semibold transition ${
                          outlineThickness === t
                            ? 'border-blush bg-blush text-white'
                            : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                        }`}
                      >
                        {t} 格
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-cocoa-light">安全距离</span>
                    {[1, 2, 3].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setOutlineGap(g)}
                        className={`rounded-lg border px-3 py-1 text-xs font-semibold transition ${
                          outlineGap === g
                            ? 'border-blush bg-blush text-white'
                            : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                        }`}
                      >
                        {g} 格
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {OUTLINE_COLORS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setOutlineColorKey(c.key)}
                        title={c.label}
                        className={`h-7 w-7 rounded-md border-2 transition ${
                          outlineColorKey === c.key
                            ? 'border-blush scale-110 shadow-sm'
                            : 'border-black/10 hover:scale-105'
                        }`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-cocoa-light">顶面文字（融入底面）</label>
              <input
                type="text"
                value={topText}
                onChange={(e) => setTopText(e.target.value)}
                maxLength={10}
                className="form-input"
                placeholder="例如：心意"
              />
            </div>
            <OptionGroup label="顶面字体" value={topFontKey} onChange={setTopFontKey} options={fontOptions} />
            <SizeSlider
              label="顶面字号（无级调节）"
              value={topHeight}
              onChange={setTopHeight}
              min={TOP_SIZE_RANGE.min}
              max={TOP_SIZE_RANGE.max}
            />
            <SwatchPicker label="顶面文字颜色" value={colorBId} onChange={setColorBId} colors={brand.colors} />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <SwatchPicker
              label="叠加区域颜色（交错处重构）"
              value={colorCId}
              onChange={(id) => { setColorCId(id); setColorCTouched(true) }}
              colors={brand.colors}
              maxHeight="max-h-20"
            />
            {colorCTouched && (
              <button
                onClick={() => setColorCTouched(false)}
                className="mt-1 text-xs text-blush hover:underline"
              >
                恢复自动推荐
              </button>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-cocoa-light">
              顶面文字微调（当前：{dx > 0 ? `右${dx}` : dx < 0 ? `左${-dx}` : '—'} / {dy > 0 ? `下${dy}` : dy < 0 ? `上${-dy}` : '—'}）
            </label>
            <div className="flex items-center gap-1.5">
              <button onClick={() => nudge(-1, 0)} className="h-9 w-9 rounded-lg border border-lavender/40 bg-white text-cocoa transition hover:border-blush" title="左移">◀</button>
              <div className="flex flex-col gap-1.5">
                <button onClick={() => nudge(0, -1)} className="h-9 w-9 rounded-lg border border-lavender/40 bg-white text-cocoa transition hover:border-blush" title="上移">▲</button>
                <button onClick={() => nudge(0, 1)} className="h-9 w-9 rounded-lg border border-lavender/40 bg-white text-cocoa transition hover:border-blush" title="下移">▼</button>
              </div>
              <button onClick={() => nudge(1, 0)} className="h-9 w-9 rounded-lg border border-lavender/40 bg-white text-cocoa transition hover:border-blush" title="右移">▶</button>
              <button
                onClick={() => { setDx(0); setDy(0) }}
                className="ml-2 rounded-lg border border-lavender/40 bg-white px-3 py-1.5 text-xs font-semibold text-cocoa transition hover:border-blush"
              >
                居中复位
              </button>
            </div>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-cocoa-light">预览</p>
          <canvas
            ref={previewCanvasRef}
            className="w-full rounded-2xl border border-lavender/30 bg-cream-100"
            style={{ imageRendering: 'pixelated' }}
          />
          <p className="mt-1 text-xs text-cocoa-light">
            {previewPattern?.width || 0}×{previewPattern?.height || 0} · {previewPattern?.legend.length || 0} 种颜色 · 共 {previewPattern?.total || 0} 豆
          </p>
        </div>

        <div className="flex items-end justify-between gap-2">
          <p className="text-[11px] leading-4 text-cocoa-light/80">
            全部字体均遵循 SIL OFL 1.1 开源协议，生成的图纸可免费用于商业用途。
          </p>
          <div className="flex shrink-0 justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-full border border-lavender/60 px-5 py-2 text-sm font-medium text-cocoa transition hover:bg-lavender/30"
          >
            取消
          </button>
          <button
            onClick={() => {
              const pattern = generatePattern()
              if (pattern) {
                onConfirm(pattern, {
                  kind: 'overlay',
                  config: {
                    bottomText, topText, bottomFontKey, topFontKey, bottomHeight, topHeight,
                    colorMode, colorAId, colorBId, colorCId, colorCTouched,
                    outlineEnabled, outlineColorKey, outlineThickness, outlineGap, dx, dy,
                  },
                })
                onClose()
              }
            }}
            disabled={!previewPattern}
            className="rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-blush-dark hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40"
          >
            确认生成
          </button>
          </div>
        </div>
      </div>
    </div>
  )
}
